import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { CheckCircle, CreditCard, DeviceMobile, Download, Receipt, XCircle } from '@phosphor-icons/react';
import { getPublicInvoice, startPublicCheckout, startSwishPayment } from '@/api/billing_public';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LogoImg } from '@/components/ui/logo-img';
import { config } from '@/config';

// Public invoice payment page (D-8). Routed at /pay/:token, OUTSIDE
// RequireAuth: the token in the URL is the credential. The page is the
// only customer-facing surface in the SPA — design system rules apply
// (lime primary, Phosphor icons, Bricolage headings) so the org's
// supporters get the same brand cues a club admin sees.
//
// The route is also reachable from the printable HTML fallback
// (server-rendered) at /api/v1/public/billing/invoices/{token}/print —
// browser print-to-PDF works there for receipts.
export function PublicInvoice() {
  const { token = '' } = useParams();
  const [searchParams] = useSearchParams();
  const checkoutOutcome = searchParams.get('checkout');
  const { t } = useTranslation('common');

  const invQuery = useQuery({
    queryKey: ['public-invoice', token],
    queryFn: () => getPublicInvoice(token),
    enabled: !!token,
    retry: (failureCount, error) => {
      // Don't retry on 404 / 410 — the token is wrong or the invoice is
      // void/draft. Only retry transient network failures.
      if (error instanceof ApiError && (error.status === 404 || error.status === 410)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const base = `${window.location.origin}/pay/${encodeURIComponent(token)}`;
      const res = await startPublicCheckout(token, `${base}?checkout=success`, `${base}?checkout=cancel`);
      return res.url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.code : 'unknown';
      setCheckoutError(msg);
    },
  });

  // Polling: when the customer returns from Stripe with ?checkout=success,
  // the webhook may still be in flight. Refetch the invoice every 2s for
  // the first ~30s so the UI flips to "paid" without a manual reload.
  useEffect(() => {
    if (checkoutOutcome !== 'success') return;
    const id = window.setInterval(() => void invQuery.refetch(), 2000);
    const stop = window.setTimeout(() => window.clearInterval(id), 30_000);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(stop);
    };
  }, [checkoutOutcome, invQuery]);

  // Swish has no redirect — the buyer scans/opens the app and the Swish
  // callback settles server-side. Once a Swish payment is started, poll the
  // invoice every 3s for ~3 min so the page flips to "paid" on its own.
  const [swishActive, setSwishActive] = useState(false);
  useEffect(() => {
    if (!swishActive) return;
    const id = window.setInterval(() => void invQuery.refetch(), 3000);
    const stop = window.setTimeout(() => window.clearInterval(id), 180_000);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(stop);
    };
  }, [swishActive, invQuery]);

  if (invQuery.isPending) {
    return (
      <PageShell>
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      </PageShell>
    );
  }

  if (invQuery.error instanceof ApiError) {
    const code = invQuery.error.code;
    return (
      <PageShell>
        <div className="flex flex-col items-center gap-3 rounded-3xl border border-border/60 bg-card p-8 text-center">
          <XCircle weight="fill" className="text-amber-600" size={48} />
          <h1 className="text-xl font-bold tracking-tight">
            {t(`billing.publicPage.error.${code}`, {
              defaultValue: t('billing.publicPage.error.unknown', {
                defaultValue: 'This invoice link is no longer valid.',
              }) ?? '',
            })}
          </h1>
        </div>
      </PageShell>
    );
  }

  const inv = invQuery.data!;
  const isPaid = inv.status === 'paid';
  const due = inv.total_minor - inv.amount_paid_minor + inv.amount_refunded_minor;

  return (
    <PageShell>
      <header className="flex flex-col gap-4 rounded-3xl border border-border/60 bg-card p-6">
        <div className="flex items-start justify-between gap-3">
          {inv.org_logo_object_key ? (
            <LogoImg
              objectKey={inv.org_logo_object_key}
              size="small"
              alt={inv.org_name}
              className="h-12 w-auto max-w-[60%] object-contain object-left"
            />
          ) : (
            <span className="flex items-center gap-2 text-lg font-bold tracking-tight">
              <Receipt weight="bold" size={28} />
              {inv.org_name}
            </span>
          )}
          <Badge
            variant={isPaid ? 'default' : inv.status === 'overdue' ? 'destructive' : 'secondary'}
          >
            {t(`billing.invoices.status.${inv.status}`, { defaultValue: inv.status })}
          </Badge>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {t('billing.publicPage.label', { defaultValue: 'Invoice' })}
          </span>
          <h1 className="text-xl font-bold tracking-tight whitespace-nowrap">{inv.number}</h1>
          {inv.ocr_reference && (
            <p className="text-sm text-muted-foreground">
              OCR <span className="font-mono">{inv.ocr_reference}</span>
            </p>
          )}
        </div>
      </header>

      <section className="rounded-3xl border border-border/60 bg-card p-6">
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t('billing.publicPage.billedTo', { defaultValue: 'Billed to' })}
        </h2>
        <p className="text-base">{inv.customer_name}</p>
        <dl className="mt-3 grid grid-cols-2 gap-y-1 text-sm text-muted-foreground">
          <dt>{t('billing.publicPage.issued', { defaultValue: 'Issued' })}</dt>
          <dd className="text-right">{inv.issue_date}</dd>
          <dt>{t('billing.publicPage.due', { defaultValue: 'Due' })}</dt>
          <dd className="text-right">{inv.due_date}</dd>
        </dl>
      </section>

      <section className="rounded-3xl border border-border/60 bg-card p-6">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t('billing.invoices.lines', { defaultValue: 'Line items' })}
        </h2>
        <ul className="flex flex-col divide-y divide-border/40">
          {inv.lines.map((l, i) => (
            <li key={i} className="flex items-center justify-between gap-3 py-3">
              <div className="flex-1 text-sm">
                <p className="font-medium">{l.description}</p>
                <p className="text-xs text-muted-foreground">
                  {(l.quantity_milli / 1000).toFixed(3)} ×{' '}
                  {formatMoney(l.unit_amount_minor, inv.currency)}
                  {l.vat_rate_bp > 0 && ` · ${(l.vat_rate_bp / 100).toFixed(2)}% VAT`}
                </p>
              </div>
              <span className="text-sm font-medium">
                {formatMoney(l.line_total_minor + l.line_vat_minor, inv.currency)}
              </span>
            </li>
          ))}
        </ul>
        <dl className="mt-4 grid grid-cols-2 gap-y-1 border-t border-border/40 pt-3 text-sm">
          <dt className="text-muted-foreground">
            {t('billing.invoices.subtotal', { defaultValue: 'Subtotal' })}
          </dt>
          <dd className="text-right">{formatMoney(inv.subtotal_minor, inv.currency)}</dd>
          <dt className="text-muted-foreground">
            {t('billing.invoices.vat', { defaultValue: 'VAT' })}
          </dt>
          <dd className="text-right">{formatMoney(inv.vat_minor, inv.currency)}</dd>
          <dt className="text-base font-semibold">
            {t('billing.invoices.total', { defaultValue: 'Total' })}
          </dt>
          <dd className="text-right text-base font-semibold">
            {formatMoney(inv.total_minor, inv.currency)}
          </dd>
          {inv.amount_paid_minor > 0 && (
            <>
              <dt className="text-muted-foreground">
                {t('billing.publicPage.paid', { defaultValue: 'Paid' })}
              </dt>
              <dd className="text-right">−{formatMoney(inv.amount_paid_minor, inv.currency)}</dd>
            </>
          )}
          {inv.amount_refunded_minor > 0 && (
            <>
              <dt className="text-muted-foreground">
                {t('billing.publicPage.refunded', { defaultValue: 'Refunded' })}
              </dt>
              <dd className="text-right">+{formatMoney(inv.amount_refunded_minor, inv.currency)}</dd>
            </>
          )}
          <dt className="text-base font-semibold">
            {t('billing.publicPage.due', { defaultValue: 'Due' })}
          </dt>
          <dd className="text-right text-base font-semibold">
            {formatMoney(Math.max(0, due), inv.currency)}
          </dd>
        </dl>
      </section>

      {isPaid ? (
        <PaidBanner token={token} />
      ) : checkoutOutcome === 'success' ? (
        <PendingBanner />
      ) : (
        <PayPanel
          due={due}
          currency={inv.currency}
          loading={checkoutMutation.isPending}
          error={checkoutError}
          onPay={() => checkoutMutation.mutate()}
          token={token}
          onSwishStarted={() => setSwishActive(true)}
        />
      )}
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 md:p-8">{children}</div>
  );
}

function PaidBanner({ token }: { token: string }) {
  const { t } = useTranslation('common');
  // Prefer the rendered PDF; the public /pdf endpoint streams the chromedp
  // output. /print is the fallback for orgs whose deployment hasn't wired
  // chromedp — it's still a valid PDF via browser print.
  const apiBase = `${config.apiURL.replace(/\/$/, '')}/api/v1/public/billing/invoices/${encodeURIComponent(token)}`;
  return (
    <div className="flex flex-col items-center gap-2 rounded-3xl bg-emerald-50 p-6 text-center text-emerald-900">
      <CheckCircle weight="fill" size={40} />
      <p className="text-lg font-semibold">
        {t('billing.publicPage.paidTitle', { defaultValue: 'Thanks — this invoice is paid.' })}
      </p>
      <p className="text-sm">
        {t('billing.publicPage.paidBody', {
          defaultValue: 'A receipt was sent to your email.',
        })}
      </p>
      <a
        className="mt-2 inline-flex items-center gap-1.5 text-sm underline"
        href={`${apiBase}/pdf`}
        target="_blank"
        rel="noreferrer"
      >
        <Download weight="bold" size={18} className="shrink-0" />
        {t('billing.publicPage.downloadReceipt', { defaultValue: 'Print or save as PDF' })}
      </a>
    </div>
  );
}

function PendingBanner() {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col items-center gap-2 rounded-3xl bg-amber-50 p-6 text-center text-amber-900">
      <p className="text-lg font-semibold">
        {t('billing.publicPage.pendingTitle', {
          defaultValue: 'Payment received — confirming with our bank.',
        })}
      </p>
      <p className="text-sm">
        {t('billing.publicPage.pendingBody', {
          defaultValue: 'This page will refresh as soon as the payment is confirmed.',
        })}
      </p>
    </div>
  );
}

function PayPanel({
  due,
  currency,
  loading,
  error,
  onPay,
  token,
  onSwishStarted,
}: {
  due: number;
  currency: string;
  loading: boolean;
  error: string | null;
  onPay: () => void;
  token: string;
  onSwishStarted: () => void;
}) {
  const { t } = useTranslation('common');
  // Prefer the chromedp-rendered PDF; falls back to /print HTML when the
  // deployment doesn't have a Chrome binary wired (browser still produces
  // a valid PDF via its print dialog).
  const apiBase = `${config.apiURL.replace(/\/$/, '')}/api/v1/public/billing/invoices/${encodeURIComponent(token)}`;
  const pdfURL = `${apiBase}/pdf`;
  const printURL = `${apiBase}/print`;

  const [swishQR, setSwishQR] = useState<string | null>(null);
  const [swishDeepLink, setSwishDeepLink] = useState<string | null>(null);
  const [swishError, setSwishError] = useState<string | null>(null);
  const swishMutation = useMutation({
    mutationFn: () => startSwishPayment(token),
    onSuccess: async (res) => {
      setSwishError(null);
      setSwishDeepLink(res.swish_deep_link || null);
      // Render the Swish payload as a scannable QR (data URL, no network).
      try {
        setSwishQR(
          await QRCode.toDataURL(res.swish_qr, { errorCorrectionLevel: 'M', margin: 1, scale: 6 }),
        );
      } catch {
        setSwishQR(null);
      }
      onSwishStarted();
    },
    onError: (e) => {
      setSwishError(e instanceof ApiError ? e.code : 'unknown');
    },
  });

  return (
    <div className="flex flex-col gap-3 rounded-3xl border border-border/60 bg-card p-6">
      <Button
        type="button"
        onClick={onPay}
        disabled={loading || due <= 0}
        className="self-stretch"
      >
        <CreditCard weight="bold" />
        {loading
          ? t('billing.publicPage.opening', { defaultValue: 'Opening Stripe…' })
          : t('billing.publicPage.payCta', {
              defaultValue: 'Pay {{amount}}',
              amount: formatMoney(due, currency),
            })}
      </Button>
      {error && (
        <p className="text-sm text-red-600">
          {t(`billing.publicPage.error.${error}`, {
            defaultValue: t('billing.publicPage.checkoutError.unknown', {
              defaultValue: 'Could not open the payment page. Try again.',
            }) ?? '',
          })}
        </p>
      )}

      {/* Swish rail. Hidden entirely once the club's rail is confirmed off. */}
      {swishError !== 'swish_not_configured' && (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => swishMutation.mutate()}
            disabled={swishMutation.isPending || due <= 0}
            className="self-stretch"
          >
            <DeviceMobile weight="bold" />
            {swishMutation.isPending
              ? t('billing.publicPage.swishStarting', { defaultValue: 'Preparing Swish…' })
              : t('billing.publicPage.swishCta', { defaultValue: 'Pay with Swish' })}
          </Button>
          {swishError && swishError !== 'swish_not_configured' && (
            <p className="text-sm text-red-600">
              {t('billing.publicPage.swishError', {
                defaultValue: 'Could not start a Swish payment. Try again or pay by card.',
              })}
            </p>
          )}
          {(swishQR || swishDeepLink) && (
            <div className="flex flex-col items-center gap-3 rounded-2xl bg-muted/40 p-4 text-center">
              {swishQR && (
                <img
                  src={swishQR}
                  alt={t('billing.publicPage.swishQrAlt', { defaultValue: 'Swish QR code' })}
                  className="h-44 w-44"
                />
              )}
              <p className="text-sm text-muted-foreground">
                {t('billing.publicPage.swishScan', {
                  defaultValue: 'Scan with the Swish app, or tap below on your phone.',
                })}
              </p>
              {swishDeepLink && (
                <a
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border border-border px-4 py-2 text-sm font-semibold"
                  href={swishDeepLink}
                >
                  <DeviceMobile weight="bold" size={18} />
                  {t('billing.publicPage.swishOpenApp', { defaultValue: 'Open the Swish app' })}
                </a>
              )}
              <p className="text-xs text-muted-foreground/70">
                {t('billing.publicPage.swishWaiting', {
                  defaultValue: 'This page updates automatically once your payment is confirmed.',
                })}
              </p>
            </div>
          )}
        </>
      )}

      <div className="flex flex-col items-center gap-1 text-sm">
        <a className="text-muted-foreground underline" href={pdfURL} target="_blank" rel="noreferrer">
          {t('billing.publicPage.downloadPDF', { defaultValue: 'Download PDF' })}
        </a>
        <a className="text-muted-foreground/70 underline text-xs" href={printURL} target="_blank" rel="noreferrer">
          {t('billing.publicPage.viewPrintable', { defaultValue: 'View printable version' })}
        </a>
      </div>
    </div>
  );
}

function formatMoney(minor: number, currency: string): string {
  if (!currency) return (minor / 100).toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}
